import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';

import {
  getGroupChildrenInfo,
  transferChildrenToNextPeriodGroup,
  removeChildrenFromNextPeriodGroup
} from './actions';

// Человекочитаемые сообщения для кодів помилок бекенду
// (addExistingChildrenToNextPeriodGroup / removeChildrenFromNextPeriodGroup)
const ERROR_MESSAGES = {
  4001: 'Відсутні обов\'язкові параметри запиту.',
  4042: 'Не вдається знайти групу з таким номером.',
  4065: 'Оберіть хоча б одну дитину.',
  4067: 'Не вдалося знайти всіх обраних дітей у поточній групі. Оновіть сторінку та спробуйте ще раз.',
  4079: 'Серед дітей вже є переведені у групу наступного навчального року.',
  4081: 'Групи повинні відноситись до послідовних навчальних періодів.',
};

const getErrorMessage = (error) => {
  if (!error) return 'Виникла помилка.';
  return ERROR_MESSAGES[error.code] || error.message || 'Виникла помилка.';
};

// Формує текст вікової категорії групи з поля group.ageLimit ({ from, to }).
const formatAgeRange = (ageLimit) => {
  if (!ageLimit) return '';
  const { from, to } = ageLimit;
  if (from === undefined && to === undefined) return '';
  if (from !== undefined && to !== undefined) return `${from}-${to} років`;
  if (from !== undefined) return `від ${from} років`;
  return `до ${to} років`;
};

// Підпис групи для випадаючого списку: номер, назва, тип групи та вікова категорія.
const formatGroupLabel = (g) => {
  const details = [g.type, formatAgeRange(g.ageLimit)].filter(Boolean).join(', ');
  return `№${g.number} - ${g.name}${details ? ` (${details})` : ''}`;
};

// Бекенд віддає дату народження як ISO-рядок (напр. 2018-09-28T00:00:00.000Z).
// Показуємо у звичному форматі дд.мм.рррр, беручи значення по UTC,
// щоб не зʼїхати на день назад/вперед через часовий пояс браузера.
const formatBirthDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

/**
 * Modal: transfer (bulk) children from a current-period group into a
 * next-period (next school year) group, and cancel such transfers.
 *
 * Props:
 *  - show, onHide
 *  - kgId              - kindergarten id
 *  - currentGroup       - the group object (current period) transfer is initiated from
 *  - allGroups          - full list of the kindergarten's groups (all periods)
 *  - transferOverrides   - { [childId]: { groupId, groupNumber, groupName } } — transfers made
 *                          earlier in this session, kept by the parent so the modal knows about
 *                          them even before `allGroups` is refetched from the server
 *  - onTransferSuccess(childrenIds, targetGroup) - called after a successful transfer
 *  - onCancelSuccess(childId)                    - called after a successful cancel
 */
const TransferToNextYearModal = ({
  show,
  onHide,
  kgId,
  currentGroup,
  allGroups = [],
  transferOverrides = {},
  onTransferSuccess,
  onCancelSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [childrenList, setChildrenList] = useState([]);
  const [transferredChildren, setTransferredChildren] = useState([]);
  const [selected, setSelected] = useState({});
  const [targetGroupNumber, setTargetGroupNumber] = useState('');

  const nextPeriodStartYear = currentGroup ? Number(currentGroup.periodStartYear) + 1 : null;

  // Групи, які відносяться до наступного навчального року
  const nextPeriodGroups = useMemo(() => {
    if (!nextPeriodStartYear) return [];
    return (allGroups || [])
      .filter(g => Number(g.periodStartYear) === nextPeriodStartYear)
      .sort((a, b) => `${a.number}`.localeCompare(`${b.number}`, undefined, { numeric: true }));
  }, [allGroups, nextPeriodStartYear]);

  const selectedTargetGroup = useMemo(
    () => nextPeriodGroups.find(g => `${g.number}` === `${targetGroupNumber}`),
    [nextPeriodGroups, targetGroupNumber]
  );

  // childId -> { _id, number, name } групи наступного року, в якій дитина вже перебуває.
  // Спочатку беремо дані з allGroups (реальні дані бекенду), потім накладаємо
  // transferOverrides — перекази, зроблені у цій сесії, на випадок якщо allGroups
  // ще не встигли оновитись.
  const nextGroupByChildId = useMemo(() => {
    const map = {};
    (allGroups || [])
      .filter(g => Number(g.periodStartYear) === nextPeriodStartYear)
      .forEach(g => {
        (g.children || []).forEach(ch => {
          map[`${ch.childId}`] = { _id: g._id, number: g.number, name: g.name, type: g.type, ageLimit: g.ageLimit };
        });
      });
    Object.entries(transferOverrides || {}).forEach(([childId, info]) => {
      map[childId] = {
        _id: info.groupId,
        number: info.groupNumber,
        name: info.groupName,
        type: info.groupType,
        ageLimit: info.groupAgeLimit
      };
    });
    return map;
  }, [allGroups, nextPeriodStartYear, transferOverrides]);

  const resetState = useCallback(() => {
    setLoading(false);
    setSubmitting(false);
    setCancellingId(null);
    setError('');
    setSuccessMessage('');
    setChildrenList([]);
    setTransferredChildren([]);
    setSelected({});
    setTargetGroupNumber('');
  }, []);

  const loadChildren = useCallback(async () => {
    if (!kgId || !currentGroup) return;
    setLoading(true);
    setError('');
    try {
      const response = await getGroupChildrenInfo(kgId, currentGroup._id);
      const list = (response && response.data) || [];

      const activeChildren = list
        .filter(ch => ch.firstName && !ch.isReserv)
        .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));

      const notTransferred = activeChildren.filter(ch => !nextGroupByChildId[`${ch._id}`]);
      const transferred = activeChildren.filter(ch => !!nextGroupByChildId[`${ch._id}`]);

      setChildrenList(notTransferred);
      setTransferredChildren(transferred);
    } catch (e) {
      console.error('Error loading children for transfer:', e);
      setError('Не вдалося завантажити список дітей групи.');
    } finally {
      setLoading(false);
    }
  }, [kgId, currentGroup, nextGroupByChildId]);

  useEffect(() => {
    if (show) {
      resetState();
      loadChildren();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, currentGroup]);

  // Автовибір групи наступного року того ж типу, що й поточна
  useEffect(() => {
    if (!show) return;
    if (!nextPeriodGroups.length) return;
    const sameType = nextPeriodGroups.find(g => g.typeNumber === currentGroup?.typeNumber);
    setTargetGroupNumber(`${(sameType || nextPeriodGroups[0]).number}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, nextPeriodGroups]);

  const toggleChild = (childId) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[childId]) {
        delete next[childId];
      } else {
        next[childId] = true;
      }
      return next;
    });
  };

  const selectAll = () => {
    const next = {};
    childrenList.forEach(ch => { next[ch._id] = true; });
    setSelected(next);
  };

  const deselectAll = () => setSelected({});

  const selectedCount = Object.keys(selected).length;

  const handleSubmit = async () => {
    if (!targetGroupNumber) {
      setError('Оберіть групу наступного навчального року для переведення.');
      return;
    }
    const childrenIds = Object.keys(selected);
    if (!childrenIds.length) {
      setError('Оберіть хоча б одну дитину для переведення.');
      return;
    }

    const targetGroup = selectedTargetGroup;

    setSubmitting(true);
    setError('');
    setSuccessMessage('');
    try {
      const response = await transferChildrenToNextPeriodGroup(
        kgId,
        currentGroup.number,
        targetGroupNumber,
        childrenIds
      );

      if (response && response.error) {
        setError(getErrorMessage(response.error));
        return;
      }

      // Переносимо дітей зі списку "не переведені" у список "переведені" локально,
      // не чекаючи оновлення allGroups з бекенду.
      const movedChildren = childrenList.filter(ch => childrenIds.includes(ch._id));
      setChildrenList(prev => prev.filter(ch => !childrenIds.includes(ch._id)));
      setTransferredChildren(prev =>
        [...prev, ...movedChildren].sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''))
      );
      setSelected({});
      setSuccessMessage(`Дітей (${childrenIds.length}) успішно переведено до групи №${targetGroup?.number || targetGroupNumber}.`);

      if (onTransferSuccess) onTransferSuccess(childrenIds, targetGroup);
    } catch (e) {
      console.error('Error transferring children:', e);
      setError('Виникла помилка під час переведення дітей. Спробуйте ще раз.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelTransfer = async (child) => {
    const targetGroup = nextGroupByChildId[`${child._id}`];
    if (!targetGroup) return;

    setCancellingId(child._id);
    setError('');
    setSuccessMessage('');
    try {
      const response = await removeChildrenFromNextPeriodGroup(kgId, targetGroup.number, [child._id]);

      if (response && response.error) {
        setError(getErrorMessage(response.error));
        return;
      }

      setTransferredChildren(prev => prev.filter(ch => ch._id !== child._id));
      setChildrenList(prev =>
        [...prev, child].sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''))
      );
      setSuccessMessage(`Переведення дитини ${child.lastName} ${child.firstName} скасовано.`);

      if (onCancelSuccess) onCancelSuccess(child._id);
    } catch (e) {
      console.error('Error cancelling transfer:', e);
      setError('Виникла помилка під час скасування переведення. Спробуйте ще раз.');
    } finally {
      setCancellingId(null);
    }
  };

  if (!currentGroup) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          Переведення дітей до групи наступного навчального року
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <p className="mb-2">
          Група: <strong>№{currentGroup.number} {currentGroup.name}</strong>
          {' '}({currentGroup.periodStartYear}-{Number(currentGroup.periodStartYear) + 1} н.р.)
        </p>

        {nextPeriodGroups.length === 0 && (
          <Alert variant="warning">
            Для наступного навчального року ({nextPeriodStartYear}-{nextPeriodStartYear + 1}) ще не створено жодної групи.
            Спочатку створіть групу наступного періоду.
          </Alert>
        )}

        {nextPeriodGroups.length > 0 && (
          <Form.Group className="mb-3">
            <Form.Label>
              Група для переведення ({nextPeriodStartYear}-{nextPeriodStartYear + 1} н.р.) <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={targetGroupNumber}
              onChange={(e) => setTargetGroupNumber(e.target.value)}
              disabled={submitting}
            >
              {nextPeriodGroups.map(g => (
                <option key={g._id} value={g.number}>
                  {formatGroupLabel(g)}
                </option>
              ))}
            </Form.Select>
            {selectedTargetGroup && (selectedTargetGroup.type || formatAgeRange(selectedTargetGroup.ageLimit)) && (
              <Form.Text className="text-muted">
                Вікова категорія обраної групи: {[selectedTargetGroup.type, formatAgeRange(selectedTargetGroup.ageLimit)].filter(Boolean).join(', ')}
              </Form.Text>
            )}
          </Form.Group>
        )}

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert variant="success" className="mb-3">
            {successMessage}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" className="me-2" />
            Завантаження списку дітей...
          </div>
        ) : (
          <>
            {childrenList.length === 0 ? (
              <Alert variant="info">
                Усі діти цієї групи вже переведені до групи наступного навчального року, або в групі немає дітей.
              </Alert>
            ) : (
              <>
                <p className="mb-1">Оберіть дітей, яких потрібно перевести:</p>
                <div className="mb-2">
                  <Button variant="link" size="sm" className="p-0 me-3" onClick={selectAll} disabled={submitting}>
                    Обрати всіх
                  </Button>
                  <Button variant="link" size="sm" className="p-0" onClick={deselectAll} disabled={submitting}>
                    Зняти всіх
                  </Button>
                </div>

                <div style={{ border: '1px solid #dee2e6', borderRadius: 4 }}>
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}></th>
                        <th>Прізвище</th>
                        <th>Ім'я</th>
                        <th>Дата народження</th>
                      </tr>
                    </thead>
                    <tbody>
                      {childrenList.map(ch => (
                        <tr
                          key={ch._id}
                          onClick={() => toggleChild(ch._id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={!!selected[ch._id]}
                              onChange={() => toggleChild(ch._id)}
                            />
                          </td>
                          <td>{ch.lastName}</td>
                          <td>{ch.firstName}</td>
                          <td>{formatBirthDate(ch.birthDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-muted mt-2 mb-0">
                  Обрано дітей: {selectedCount} з {childrenList.length}
                </p>
              </>
            )}

            {transferredChildren.length > 0 && (
              <div className="mt-4">
                <p className="mb-1">Вже переведені з цієї групи:</p>
                <div style={{ border: '1px solid #dee2e6', borderRadius: 4 }}>
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Прізвище</th>
                        <th>Ім'я</th>
                        <th>Дата народження</th>
                        <th>Переведено до групи</th>
                        <th style={{ width: 140 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {transferredChildren.map(ch => {
                        const targetGroup = nextGroupByChildId[`${ch._id}`];
                        return (
                          <tr key={ch._id}>
                            <td>{ch.lastName}</td>
                            <td>{ch.firstName}</td>
                            <td>{formatBirthDate(ch.birthDate)}</td>
                            <td>{targetGroup ? `№${targetGroup.number} ${targetGroup.name || ''}${formatAgeRange(targetGroup.ageLimit) ? ` (${formatAgeRange(targetGroup.ageLimit)})` : ''}` : '—'}</td>
                            <td>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                disabled={cancellingId === ch._id}
                                onClick={() => handleCancelTransfer(ch)}
                              >
                                {cancellingId === ch._id ? (
                                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                ) : (
                                  'Скасувати'
                                )}
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting} type="button">
          Закрити
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={submitting || loading || !childrenList.length || !nextPeriodGroups.length}
          type="button"
        >
          {submitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
              Переведення...
            </>
          ) : (
            'Перевести обраних дітей'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TransferToNextYearModal;